import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { listTalleres, createTaller, updateTaller, deleteTaller } from '../redux/actions/talleres';
import GestionarTallerModal from './GestionarTallerModal';
import '../css/GestionarTaller.css';
import { TALLER_SEPARATOR } from '../consts';

const toDateInputValue = (value) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

const GestionarTaller = () => {
  const dispatch = useDispatch();
  const { talleres, loading, error } = useSelector((state) => state.Taller);

  const [formValues, setFormValues] = useState({
    id: '',
    nombre: '',
    descripcion: '',
    fechaInicio: '',
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    dispatch(listTalleres());
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
    if (name === 'nombre' && formError) {
      setFormError('');
    }
  };

  const validateTallerName = (nombre) => {
    if (!nombre || !String(nombre).trim()) {
      return 'El nombre del taller es obligatorio.';
    }
    if (String(nombre).includes(TALLER_SEPARATOR)) {
      return `El nombre del taller no puede contener el carácter "${TALLER_SEPARATOR}" porque se usa como separador de talleres.`;
    }
    return null;
  };

  const handleCreateTaller = async (e) => {
    e.preventDefault();
    const { nombre, descripcion, fechaInicio } = formValues;
    const err = validateTallerName(nombre);
    if (err) {
      setFormError(err);
      return;
    }
    if (!fechaInicio) {
      setFormError('La fecha de inicio es obligatoria.');
      return;
    }
    const result = await dispatch(createTaller(nombre.trim(), descripcion, fechaInicio));
    if (result && result.success) {
      setFormError('');
      dispatch(listTalleres()); // Reload the taller list
      closeModal();
    } else {
      setFormError((result && result.detail) || 'Error al crear el taller.');
    }
  };

  const handleUpdateTaller = async (e) => {
    e.preventDefault();
    const { id, nombre, descripcion, fechaInicio } = formValues;
    const err = validateTallerName(nombre);
    if (err) {
      setFormError(err);
      return;
    }
    if (!fechaInicio) {
      setFormError('La fecha de inicio es obligatoria.');
      return;
    }
    const result = await dispatch(updateTaller(id, nombre.trim(), descripcion, fechaInicio));
    if (result && result.success) {
      setFormError('');
      dispatch(listTalleres()); // Reload the taller list
      closeModal();
    } else {
      setFormError((result && result.detail) || 'Error al actualizar el taller.');
    }
  };

  const openCreateModal = () => {
    setIsEditMode(false);
    setFormError('');
    setFormValues({
      id: '',
      nombre: '',
      descripcion: '',
      fechaInicio: '',
    });
    setIsModalOpen(true);
  };

  const openEditModal = (taller) => {
    setIsEditMode(true);
    setFormError('');
    setFormValues({
      id: taller.id,
      nombre: taller.nombre,
      descripcion: taller.descripcion,
      fechaInicio: toDateInputValue(taller.fecha_inicio),
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setFormError('');
    setFormValues({
      id: '',
      nombre: '',
      descripcion: '',
      fechaInicio: '',
    });
  };

  const handleDeleteTaller = (id) => {
    dispatch(deleteTaller(id)).then(() => {
      dispatch(listTalleres());
    });
  };

  const sortedTalleres = talleres ? [...talleres].sort((a, b) => a.nombre.localeCompare(b.nombre)) : [];

  return (
    <div className="GT-container">
      <div className="GT-inner-container">
        <h2 className="GT-title">Gestionar Talleres</h2>

        {loading && <p className="GT-loading">Loading...</p>}
        {error && <p className="GT-error">Error: {error}</p>}

        <button className="GT-create-button" onClick={openCreateModal}>
          Crear Taller
        </button>

        <GestionarTallerModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSubmit={isEditMode ? handleUpdateTaller : handleCreateTaller}
          formValues={formValues}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          formError={formError}
        />

        <h3 className="GT-subtitle">Lista de Talleres</h3>
        <ul className="GT-taller-list">
          {sortedTalleres.map((taller) => (
            <li key={taller.id} className="GT-taller-item">
              <span className="GT-taller-info">{taller.nombre}</span>
              <div className="GT-taller-actions">
                <button className="GT-edit-button" onClick={() => openEditModal(taller)}>Editar</button>
                <button className="GT-delete-button" onClick={() => handleDeleteTaller(taller.id)}>Eliminar</button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default GestionarTaller;
